import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { useBilling, useCheckout } from '@/app/queries';
import { formatDate } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Chip } from '@/ui/Chip';
import { Notice, Panel, Skeleton } from '@/ui/Panel';
import { Section } from '@/ui/Section';

/**
 * What the club is on, what it has left, and how to get more.
 *
 * Shown to the coach rather than kept in the admin, because the two numbers
 * that decide whether a feature works for him — his plan and his credits — are
 * his. A product that makes him ask what he is paying for is one he stops
 * paying for.
 *
 * Nothing is purchasable until an admin lists the products, so an
 * unconfigured deployment shows the plan and no buttons rather than a checkout
 * that fails after he has decided to spend money.
 */
export function Billing({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const billing = useBilling();
  const checkout = useCheckout();
  const [failed, setFailed] = useState(false);

  // A hole in the page is worse than the shape of one, and this panel is about
  // what the club is paying.
  if (!billing.data) {
    return (
      <Panel title={t('settings.billing')}>
        <Skeleton lines={4} />
      </Panel>
    );
  }

  const { plan, credits, limits, products, status, interval, currentPeriodEnd } = billing.data;

  const buy = async (productId: string) => {
    setFailed(false);
    try {
      const { url } = await checkout.mutateAsync({ productId, returnPath: `/${locale}/settings` });
      // A full navigation to Polar's own page, never an embed: the card form
      // stays on their origin and never touches ours.
      window.location.href = url;
    } catch {
      setFailed(true);
    }
  };

  const plans = products.filter((p) => p.kind === 'plan');
  const packs = products.filter((p) => p.kind === 'credits');

  return (
    <Panel
      title={t('settings.billing')}
      collapsible
      storeKey="settings.billing"
      defaultOpen={false}
      // Closed, it still says the plan and the balance — which is all a coach
      // usually wants from this panel. Open, it is the full picture and the
      // buttons.
      summary={`${t(`admin.plan_${plan}`)} · ${t('ai.balanceLeft', { count: credits })}`}
    >
      <Section title={t('settings.yourPlan')}>
        <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-meta text-ink-muted">{t('settings.plan')}</div>
            <div className="flex items-center gap-2">
              <Chip>{t(`admin.plan_${plan}`)}</Chip>
              {status && status !== 'active' ? <Chip muted>{status}</Chip> : null}
            </div>
          </div>
          <div>
            <div className="text-meta text-ink-muted">{t('settings.credits')}</div>
            <div className="tabular font-condensed text-headline font-bold">{credits}</div>
          </div>
          <div>
            <div className="text-meta text-ink-muted">{t('settings.included')}</div>
            <div className="text-meta">
              {t('settings.allowance', { count: limits.monthlyCredits })}
            </div>
          </div>
          <div>
            <div className="text-meta text-ink-muted">{t('settings.renews')}</div>
            <div className="tabular text-meta">
              {currentPeriodEnd
                ? formatDate(new Date(currentPeriodEnd).toISOString().slice(0, 10), locale)
                : '—'}
              {interval ? ` · ${interval}` : ''}
            </div>
          </div>
        </div>

        <p className="mt-3 max-w-[70ch] text-meta text-ink-muted">
          {t('settings.limitsLine', {
            teams: limits.maxTeams,
            players: limits.maxPlayersPerTeam,
          })}
        </p>
      </Section>

      {plan !== 'pro' && plans.length > 0 ? (
        <Section title={t('settings.upgrade')} hint={t('settings.upgradeHint')}>
          <div className="flex flex-wrap gap-2">
            {plans.map((p) => (
              <Button
                key={p.id}
                type="button"
                variant="primary"
                busy={checkout.isPending}
                onClick={() => void buy(p.id)}
              >
                {p.label} · ${p.priceUsd.toFixed(2)}
              </Button>
            ))}
          </div>
        </Section>
      ) : null}

      {packs.length > 0 ? (
        <Section title={t('settings.topUp')} hint={t('settings.topUpHint')}>
          <div className="flex flex-wrap gap-2">
            {packs.map((p) => (
              <Button
                key={p.id}
                type="button"
                variant="secondary"
                busy={checkout.isPending}
                onClick={() => void buy(p.id)}
              >
                {p.label} · ${p.priceUsd.toFixed(2)}
              </Button>
            ))}
          </div>
        </Section>
      ) : null}

      {failed ? (
        <div className="px-4 pb-4">
          <Notice tone="risk">{t('settings.checkoutFailed')}</Notice>
        </div>
      ) : null}
    </Panel>
  );
}
